import React, { useState, useEffect } from 'react';
import { urlFor } from '../../utils/sanityClient';
import { FiCalendar, FiTrendingUp, FiPlay, FiHeart } from 'react-icons/fi';
import { getYouTubeThumbnailUrl, getYouTubeThumbnailFallbackUrl, isYouTubeUrl, getYouTubeEmbedUrl } from '../../utils/videoUtils';
import Modal from '../Modal';
import { useFavorites } from '../../hooks/useFavorites';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface MovieHeroProps {
  id: string; // Added movie ID
  title: string;
  releaseDate: string;
  hypeLevel: number;
  poster: any;
  posterCopyright: string;
  synopsis: string;
  synopsisCopyright?: string;
  trailer?: {
    videoUrl: string;
    platform: string;
  };
}

const MovieHero: React.FC<MovieHeroProps> = ({
  id, // Added movie ID
  title,
  releaseDate,
  hypeLevel,
  poster,
  posterCopyright,
  synopsis,
  synopsisCopyright,
  trailer,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user: isSignedIn } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formattedDate = new Date(releaseDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const toggleSynopsis = () => {
    setIsExpanded(!isExpanded);
  };

  const handleTrailerClick = (e: React.MouseEvent) => {
    if (!trailer) return;
    
    if (isMobile) {
      // On mobile, let the default link behavior work
      return;
    }

    // On desktop, prevent default and open modal
    e.preventDefault();
    setIsModalOpen(true);
  };

  return (
    <div className="relative pt-14 md:pt-20 min-h-[80vh] md:min-h-[70vh] flex items-end pb-8 md:pb-12">
      {/* Background Image with Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src={urlFor(poster)}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="container-custom relative z-10">
        <div className="flex flex-col md:flex-row gap-3 md:gap-8 items-start">
          {/* Poster with Copyright */}
          <div className="w-48 md:w-64 mx-auto md:mx-0 -mt-auto md:mt-0 flex-shrink-0 rounded-lg overflow-hidden shadow-2xl relative z-20">
            <img
              src={urlFor(poster)}
              alt={title}
              className="w-full h-auto"
            />
            <div className="text-xs text-white/70 bg-black/50 py-1 px-2 text-center">
              Image © {posterCopyright}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white">
                {title}
              </h1>
              {isSignedIn && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSignedIn) {
                      toggleFavorite(id);
                    } else {
                      navigate('/sign-in');
                    }
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white transition-colors"
                  aria-label={isFavorite(id) ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <FiHeart className={`w-6 h-6 ${isFavorite(id) ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                </button>
              )}
            </div>

            <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-4 text-white/80">
              <div className="flex items-center gap-2">
                <FiCalendar className="w-5 h-5" />
                <span>{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiTrendingUp className="w-5 h-5 text-accent-teal" />
                <span className="font-semibold text-accent-teal">
                  {Math.round(hypeLevel)} Hype Score
                </span>
              </div>
            </div>

            <div className="relative">
              <p className={`text-base md:text-lg text-white/90 ${isExpanded ? '' : 'line-clamp-3'} md:line-clamp-none transition-all duration-200`}>
                {synopsis}
              </p>
              {synopsisCopyright && (
                <div className="text-xs text-white/50 mt-1">
                  Synopsis © {synopsisCopyright}
                </div>
              )}
              <button
                onClick={toggleSynopsis}
                className="md:hidden mt-2 text-accent-teal font-medium hover:text-accent-teal/80 transition-colors duration-200"
              >
                {isExpanded ? 'Show less' : 'Read more'}
              </button>
            </div>

            {/* Trailer Thumbnail */}
            {trailer && isYouTubeUrl(trailer.videoUrl) && (
              <div className="mt-6 relative w-full max-w-xl rounded-lg overflow-hidden shadow-lg">
                <img
                  src={getYouTubeThumbnailUrl(trailer.videoUrl)!}
                  alt={`${title} trailer thumbnail`}
                  className="w-full h-auto"
                  onError={(e) => {
                    const fallbackUrl = getYouTubeThumbnailFallbackUrl(trailer.videoUrl);
                    if (fallbackUrl && e.currentTarget.src !== fallbackUrl) {
                      e.currentTarget.src = fallbackUrl;
                    }
                  }}
                />
                <a
                  href={trailer.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTrailerClick}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/60 transition-colors duration-200"
                >
                  <div className="w-16 h-16 rounded-full bg-accent-teal/90 flex items-center justify-center">
                    <FiPlay className="w-8 h-8 text-white translate-x-0.5" />
                  </div>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal for Desktop */}
      {trailer && isYouTubeUrl(trailer.videoUrl) && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <iframe
            src={getYouTubeEmbedUrl(trailer.videoUrl) + "?autoplay=1"}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Modal>
      )}
    </div>
  );
};

export default MovieHero;